import { useEffect, useState } from "react";
import { Save, Wallet, Link2, DollarSign, Bitcoin, ArrowRightLeft, Settings2, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import api from "../lib/api";

export default function ConfigDeposit() {
  const { form, set, save, loading } = useSettings();

  // Exchange rate (kept in crypto-config, shown here for quick access)
  const [rate, setRate] = useState("");
  const [rateLoading, setRateLoading] = useState(true);
  const [rateSaving, setRateSaving] = useState(false);
  const [cryptoEnabled, setCryptoEnabled] = useState(true);
  const [coinCount, setCoinCount] = useState({ enabled: 0, total: 0 });

  // Gateway summary (from /site/admin/gateways)
  const [gateways, setGateways] = useState([]);
  const [gwLoading, setGwLoading] = useState(true);

  const reloadCrypto = async () => {
    try {
      const { data } = await api.get("/site/admin/crypto-config");
      setRate(String(data?.rate_bdt_per_usd ?? 120));
      setCryptoEnabled(!!data?.enabled);
      const coins = data?.coins || {};
      const total = Object.keys(coins).length;
      const enabled = Object.values(coins).filter((c) => c?.enabled).length;
      setCoinCount({ enabled, total });
    } catch {
      // silent
    } finally {
      setRateLoading(false);
    }
  };
  const reloadGateways = async () => {
    setGwLoading(true);
    try {
      const { data } = await api.get("/site/admin/gateways");
      setGateways(data?.items || []);
    } catch {
      setGateways([]);
    } finally {
      setGwLoading(false);
    }
  };

  useEffect(() => {
    reloadCrypto();
    reloadGateways();
  }, []);

  const saveRate = async () => {
    const r = Number(rate);
    if (!r || r <= 0) return toast.error("Exchange rate must be greater than 0");
    setRateSaving(true);
    try {
      await api.put("/site/admin/crypto-config", { rate_bdt_per_usd: r });
      toast.success(`Exchange rate updated · 1 USD = ৳${r}`);
      reloadCrypto();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save rate");
    } finally {
      setRateSaving(false);
    }
  };

  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  // E-wallet vs crypto gateway split
  const ewalletGw = gateways.filter((g) => Array.isArray(g.methods) && g.methods.some((m) => ["bkash", "nagad", "rocket", "upay"].includes(m)));
  const cryptoGw = gateways.filter((g) => g.provider === "nowpayments");
  const enabledCount = (gateways || []).filter((g) => g.enabled).length;

  return (
    <div data-testid="config-deposit-page">
      <PageHeader title="Configuration" subtitle="Deposit Page · controls, exchange rate, and per-gateway access" />
      <ConfigTabs />
      <div className="space-y-5 max-w-5xl">
        {/* Master controls */}
        <Section title="Deposit Page Controls" icon={Wallet}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle
              label="Deposits enabled"
              hint="Master kill switch shown to players on /profile/deposit"
              checked={!!form.deposit_enabled}
              onChange={(v) => set("deposit_enabled", v)}
              testid="cfg-dp-enabled"
            />
            <div className="p-3 border border-emerald-100 rounded-sm bg-emerald-50/40 text-xs text-gray-700 leading-relaxed">
              <div className="font-semibold text-gray-900 mb-1 inline-flex items-center gap-1.5"><Link2 size={13} /> Per-gateway settings</div>
              Individual channels (bKash / Nagad / Rocket / banks / crypto) — limits, provider keys, fees — are managed in{" "}
              <Link to="/root/admin/payment/gateways" className="text-emerald-600 font-semibold underline" data-testid="cfg-dp-gateways-link">Payment Method → Gateways</Link>.
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Notice shown on deposit page</label>
            <textarea
              rows={2}
              value={form.deposit_notice || ""}
              onChange={(e) => set("deposit_notice", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
              placeholder="Funds credit automatically after the gateway confirms."
              data-testid="cfg-dp-notice"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Global Min Deposit (fallback)">
              <input type="number" step="0.01" value={form.min_deposit ?? 0} onChange={(e) => set("min_deposit", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-dp-min" />
            </Field>
            <Field label="Global Max Deposit (fallback)">
              <input type="number" step="0.01" value={form.max_deposit ?? 0} onChange={(e) => set("max_deposit", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-dp-max" />
            </Field>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="BDT — auto-reject after"
              hint="Pending bKash / Nagad / Rocket / UPay deposits older than this are silently rejected. No email or notification is sent. Set to Disabled to keep them pending until manual action."
            >
              <select
                value={String(form.deposit_auto_reject_hours_bdt ?? form.deposit_auto_reject_hours ?? 12)}
                onChange={(e) => set("deposit_auto_reject_hours_bdt", parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white"
                data-testid="cfg-dp-auto-reject-bdt"
              >
                <option value="0">Disabled (never auto-reject)</option>
                <option value="6">6 hours</option>
                <option value="12">12 hours (default)</option>
                <option value="24">24 hours</option>
                <option value="48">48 hours</option>
                <option value="72">72 hours</option>
              </select>
            </Field>
            <Field
              label="Crypto — auto-reject after"
              hint="Pending site crypto (USDT / BTC / ETH / TON / TRX) and agent USDT deposits older than this are silently rejected. Crypto confirmations can be slow — keep this longer than the BDT window."
            >
              <select
                value={String(form.deposit_auto_reject_hours_crypto ?? 48)}
                onChange={(e) => set("deposit_auto_reject_hours_crypto", parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white"
                data-testid="cfg-dp-auto-reject-crypto"
              >
                <option value="0">Disabled (never auto-reject)</option>
                <option value="6">6 hours</option>
                <option value="12">12 hours</option>
                <option value="24">24 hours</option>
                <option value="48">48 hours (default)</option>
                <option value="72">72 hours</option>
                <option value="168">7 days</option>
              </select>
            </Field>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={loading}
              className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="cfg-dp-save"
            >
              <Save size={14} /> {loading ? "Saving..." : "Save Deposit Settings"}
            </button>
          </div>
        </Section>

        {/* Exchange Rate (quick edit, prominent) */}
        <Section title="Exchange Rate (BDT ⇄ USD)" icon={ArrowRightLeft} accent="amber">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
                <DollarSign size={13} className="text-amber-500" /> 1 USD = ? BDT
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold pointer-events-none">৳</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="120"
                    disabled={rateLoading}
                    className="w-full h-11 pl-7 pr-3 border border-amber-200 rounded-sm text-base font-bold tabular focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:opacity-60"
                    data-testid="cfg-dp-rate-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={saveRate}
                  disabled={rateSaving || rateLoading || !rate}
                  className="h-11 px-5 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="cfg-dp-rate-save"
                >
                  <Save size={14} /> {rateSaving ? "Saving…" : "Save Rate"}
                </button>
                <button
                  type="button"
                  onClick={reloadCrypto}
                  disabled={rateLoading}
                  title="Reload"
                  className="h-11 w-11 grid place-items-center border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCcw size={14} />
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">Used for crypto deposit / withdraw conversion (USDT, USDC, BTC, ETH, BNB, TRX, TON). Affects what player sees as "≈ $X USD" on the deposit page.</p>
            </div>
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <div className="font-semibold mb-1">Live Preview</div>
              <div className="space-y-0.5 text-[11.5px]">
                <div>৳1,000 ≈ <b>{Number(rate) > 0 ? (1000 / Number(rate)).toFixed(2) : "—"}</b> USD</div>
                <div>৳20,000 ≈ <b>{Number(rate) > 0 ? (20000 / Number(rate)).toFixed(2) : "—"}</b> USD</div>
                <div>100 USD = <b>৳{Number(rate) > 0 ? (100 * Number(rate)).toLocaleString() : "—"}</b></div>
              </div>
            </div>
          </div>
        </Section>

        {/* E-wallet methods overview */}
        <Section title="E-wallet Channels Overview" icon={Wallet}>
          {gwLoading ? (
            <div className="text-sm text-gray-500 py-4">Loading channels…</div>
          ) : ewalletGw.length === 0 ? (
            <div className="text-sm text-gray-500 py-4">No e-wallet gateways configured. Add one in <Link to="/root/admin/payment/gateways" className="text-emerald-600 font-semibold underline">Gateways</Link>.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Provider</th>
                    <th className="text-left px-3 py-2">Methods</th>
                    <th className="text-right px-3 py-2">Min</th>
                    <th className="text-right px-3 py-2">Max</th>
                    <th className="text-center px-3 py-2 w-[110px]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ewalletGw.map((g) => (
                    <tr key={g.id} className="border-t border-gray-100" data-testid={`cfg-dp-gw-${g.id}`}>
                      <td className="px-3 py-2 font-semibold text-gray-800">{g.label || g.provider}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(g.methods || []).map((m) => (
                            <span key={m} className="inline-block text-[10px] uppercase bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{m}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 tabular">৳{Number(g.min_amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-gray-600 tabular">{g.max_amount ? `৳${Number(g.max_amount).toLocaleString()}` : "∞"}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${g.enabled ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{g.enabled ? "Live" : "Off"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 text-[11.5px] text-gray-500">
                {enabledCount} of {gateways.length} total gateways enabled. Edit fees, keys & limits in <Link to="/root/admin/payment/gateways" className="text-emerald-600 font-semibold underline">Gateways</Link>.
              </div>
            </div>
          )}
        </Section>

        {/* Crypto coins quick view */}
        <Section title="Crypto Deposit Channels" icon={Bitcoin} accent="amber">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${cryptoEnabled ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{cryptoEnabled ? "Enabled" : "Disabled"}</span>
            <span className="text-sm text-gray-700"><b>{coinCount.enabled}</b> of <b>{coinCount.total}</b> coins active</span>
            <span className="text-sm text-gray-700">Gateway: <b>{cryptoGw.length > 0 ? cryptoGw[0].label : "—"}</b></span>
            <Link
              to="/root/admin/configuration/crypto"
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-xs font-semibold"
              data-testid="cfg-dp-crypto-link"
            >
              <Settings2 size={13} /> Configure coins & limits
            </Link>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-gray-500 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, accent = "emerald", children }) {
  const accentColor = accent === "amber" ? "text-amber-500" : "text-emerald-500";
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className={accentColor} />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
